import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Heart, Users } from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

export default function Equipo() {
  const valores = [
    { icon: Award, title: "Formación especializada", desc: "Todo el equipo tiene formación específica en alta capacidad, desarrollo emocional y metodología activa." },
    { icon: Heart, title: "Vocación genuina", desc: "Personas que eligen estar aquí porque entienden desde dentro lo que significa ser diferente." },
    { icon: Users, title: "Equipo cohesionado", desc: "Trabajamos como un equipo real, no como un conjunto de monitores. Eso se nota en el ambiente del campamento." },
  ];

  return (
    <main data-testid="equipo-page" style={{ fontFamily: "Manrope, sans-serif" }}>
      <section className="pt-32 pb-16 px-5 lg:px-8" style={{ background: "#F0F9FF" }}>
        <div className="max-w-3xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="section-label mb-5">El equipo</div>
            <h1
              style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F", lineHeight: 1.2 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              Las personas detrás
              <br />
              <em style={{ color: "#1B5F7E" }}>de DAC-PAC.</em>
            </h1>
            <p className="text-slate-500 leading-relaxed max-w-xl mx-auto">
              Un equipo seleccionado por su formación, su experiencia y, sobre todo, por su capacidad de conectar con personas que funcionan diferente.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="relative"
            >
              <div className="rounded-3xl overflow-hidden shadow-2xl" style={{ height: "520px" }}>
                <img
                  src="/images/equipo/jana-martinez-piqueras.png"
                  alt="Directora DAC-PAC"
                  className="w-full h-full object-cover object-center"
                />
              </div>
              <div className="absolute -bottom-5 -right-4 rounded-2xl px-5 py-4 shadow-xl" style={{ background: "#1A2B3C" }}>
                <div className="text-xs font-bold uppercase tracking-widest text-[#B8D9E8] mb-1" style={{ fontFamily: "Manrope, sans-serif" }}>Dirección</div>
                <div style={{ fontFamily: "Playfair Display, serif", color: "white", fontSize: "1rem" }}>Alta capacidad</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <div className="section-label mb-5" style={{ color: "#1B5F7E" }}>Fundadora y directora</div>
              <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold mb-2">
                Jana Martínez-Piqueras
              </h2>
              <p className="text-[#1B5F7E] font-medium text-sm mb-6" style={{ fontFamily: "Manrope, sans-serif" }}>
                Especialista en alta capacidad · Fundadora de DAC-PAC
              </p>

              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Maestra de Educación Infantil y Primaria, con mención en Pedagogía Terapéutica, cuenta con más de 30 años de experiencia en el ámbito educativo, diez de ellos dedicados íntegramente al trabajo con personas con alta capacidad.
                </p>
                <p>
                  A lo largo de su trayectoria ha impulsado iniciativas pioneras en este campo. Ha desarrollado e implementado programas de AACC en diferentes centros educativos y fue cofundadora de la Unidad de Altas Capacidades de Sanitas, proyectos que han contribuido a visibilizar y acompañar mejor a estos perfiles dentro y fuera del ámbito escolar.
                </p>
                <p>
                  Actualmente preside la Fundación Indifferent Minds, desde donde trabaja en la formación de profesionales y en el acompañamiento a familias, promoviendo una mirada más profunda y respetuosa hacia la alta capacidad.
                </p>
                <p>
                  DAC-PAC nace de esa misma convicción: crear un espacio seguro, estimulante y libre de juicios, donde niños, adolescentes y adultos con alta capacidad puedan desarrollarse con libertad, compartir con iguales y encontrar el acompañamiento que muchas veces el sistema educativo tradicional aún no ofrece.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                {["Alta capacidad", "Educación", "Pedagogía Terapéutica", "Formación de profesionales", "Acompañamiento a familias"].map((tag, i) => (
                  <span
                    key={i}
                    className="px-4 py-1.5 rounded-full text-xs font-medium"
                    style={{ background: "#EBF5FB", color: "#1B5F7E", fontFamily: "Manrope, sans-serif" }}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 px-5 lg:px-8" style={{ background: "#F8FAFC" }}>
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            variants={fadeUp}
            className="text-center mb-12"
          >
            <div className="section-label mb-4">Nuestros valores</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold">
              Lo que nos define como equipo
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {valores.map((v, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                variants={fadeUp}
                className="p-7 rounded-2xl bg-white border border-slate-100"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-5" style={{ background: "#EBF5FB" }}>
                  <v.icon size={18} style={{ color: "#1B5F7E" }} />
                </div>
                <h4 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-lg font-semibold mb-3">{v.title}</h4>
                <p className="text-slate-500 text-sm leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-5 lg:px-8" style={{ background: "#FFFFFF" }}>
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            variants={fadeUp}
            className="text-center mb-12"
          >
            <div className="section-label mb-4">El equipo completo</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-3xl md:text-4xl font-semibold mb-4">
              Los monitores que hacen posible DAC-PAC
            </h2>
            <p className="text-slate-500 max-w-2xl mx-auto leading-relaxed">
              Un equipo joven, formado y con experiencia en alta capacidad. Personas que no solo acompañan:
              muchos de ellos conocen desde dentro lo que significa crecer siendo diferente.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl overflow-hidden shadow-xl mb-12"
            style={{ maxHeight: "500px" }}
          >
            <img
              src="/images/equipo/equipo-dacpac.jpeg"
              alt="Equipo de monitores DAC-PAC"
              className="w-full object-cover object-center"
              style={{ height: "500px" }}
              data-testid="equipo-foto-grupo"
            />
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mt-8">
            <div>
              <h3 style={{ fontFamily: "Playfair Display, serif", color: "#0D3A4F" }} className="text-2xl font-semibold mb-5">
                Un equipo que lo ha vivido
              </h3>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  El equipo de monitores de DAC-PAC está formado por personas con alta capacidad, de ahí también el origen de nuestro nombre: de alta capacidad para alta capacidad. Muchos de ellos comparten este perfil, lo que les permite comprender desde dentro las inquietudes, intereses y formas de pensar de los participantes.
                </p>
                <p>
                  Se trata de un equipo multidisciplinar, con perfiles procedentes de ámbitos como la psicología, la psicopedagogía, la educación o distintas áreas del conocimiento. Todos los monitores pasan por un proceso de selección en el que se valora tanto su formación como su sensibilidad y su capacidad para crear un entorno estimulante y respetuoso.
                </p>
                <p>
                  Más que monitores, son personas que acompañan, comparten y construyen comunidad con los participantes.
                </p>
              </div>
            </div>
            <div className="space-y-5">
              {[
                { num: "5:1", desc: "Ratio máximo de participantes por monitor" },
                { num: "100%", desc: "Del equipo con formación específica en alta capacidad" },
                { num: "Equipo experimentado", desc: "" },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="flex items-center gap-5 p-6 rounded-2xl"
                  style={{ background: "#EBF5FB", marginBottom: "8px" }}
                >
                  <div style={{ fontFamily: "Playfair Display, serif", color: "#1B5F7E", fontSize: "1.8rem", fontWeight: 700, minWidth: "80px" }}>
                    {stat.num}
                  </div>
                  <div className="text-slate-600 text-sm leading-snug">{stat.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-5 lg:px-8" style={{ background: "#0D3A4F" }}>
        <div className="max-w-2xl mx-auto text-center">
          <h2 style={{ fontFamily: "Playfair Display, serif", color: "#FFFFFF" }} className="text-3xl md:text-4xl font-semibold mb-5">
            ¿Quieres que tu hijo esté con nosotros?
          </h2>
          <p className="text-[#B8D9E8] mb-10 leading-relaxed">
            Las plazas son limitadas. Solicita preinscripción y te contactaremos personalmente.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/preinscripcion" data-testid="equipo-preinscripcion-cta" className="btn-primary">
              Solicitar preinscripción <ArrowRight size={16} />
            </Link>
            <Link to="/contacto" style={{ color: "#B8D9E8", borderColor: "#B8D9E8" }} className="btn-outline">
              Contactar
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
